import {
    createRouter,
    createWebHistory,
    type RouteLocationNormalizedGeneric,
    type RouteRecordRaw,
} from "vue-router";

import {
    readVariantParam,
    resolveVariantId,
    variantMetaFromParam,
} from "@src/config/variants";
import { useSpeedtestStore } from "@features/speedtest";
import { useAdminAuthStore } from "@src/stores/useAdminAuthStore.ts";

/**
 * Records that are inapplicable on the dpi variant. dpi is a hostname overlay
 * (`dpi.` prefix) with no survey/chooser flow and no school/home audiences, so
 * any navigation into a survey-gated record redirects to `/`.
 *
 * DDR-AV-ROUTE — this is now a MATCHED-RECORD test, not a route-NAME-set test.
 * The four flat variant records collapsed to ONE parameterized record, so the
 * old `SURVEY_GATED_ROUTES` name-set (`speedtest-only`/`speedtest-school`/…)
 * silently stopped matching and the dpi gate FAILED OPEN (verified P0). The
 * guard below replaces it: it reads `to.params.variant` (the school/home
 * audiences) + `to.matched` (the survey/thankyou records) directly. The names
 * here are stable, param-independent records — safe to key by name.
 */
const SURVEY_GATED_RECORD_NAMES = new Set<string>(["survey", "thankyou"]);

/**
 * The variant param values that are inapplicable on the dpi variant: the
 * school/home audiences carry a survey flow dpi has no surface for. `test`
 * (speedtest-only) and the bare `/` path stay allowed — dpi IS a speedtest-
 * only deployment.
 */
const SURVEY_GATED_VARIANT_PARAMS = new Set<string>(["school", "home"]);

/**
 * `result`'s navigation guard (DDR-AV-ROUTE obligation #4). A cold deep-link or
 * hard refresh onto `/result` (or `/school/result`, …) lands with an EMPTY
 * engine store — the URL is real but there is no completed result to show. The
 * guard holds the state gate: it redirects to the running route (same variant)
 * unless the engine store's phase is `complete`. The store is an app-scoped
 * Pinia singleton, so reading it inside the guard reflects the live run.
 */
function resultGuard(to: RouteLocationNormalizedGeneric) {
    const speedtest = useSpeedtestStore();
    if (speedtest.currentPhase === "complete") return true;
    return { name: "speedtest" as const, params: to.params };
}

const routes: RouteRecordRaw[] = [
    // ── Speedtest flow (DDR-AV-ROUTE: one parameterized group) ───────────
    //
    // The four flat records (`/`, `/test`, `/school`, `/home`) collapse to
    // ONE parameterized record `/:variant(test|school|home)?` mounting
    // `SpeedtestView`, with two children encoding the engine STATE in the URL:
    //   • `""`     → the running stage  (`speedtest`)
    //   • `result` → the complete stage (`speedtest-result`)
    // Variant URL identity is preserved — `/`, `/test`, `/school`, `/home`
    // and their `/…/result` siblings all resolve and are deep-linkable /
    // refresh-survivable ("completion is not a destination", I-3, closed).
    // The variant no longer lives in a per-record `meta`; it is the
    // `:variant` param (`variantMetaFromParam` maps it for the registry).
    {
        // The parent is a PURE grouping record (path-prefix + param decl, no
        // component): App.vue's top-level `<RouterView>` renders the matched
        // LEAF, and both leaves carry `SpeedtestView`. A parent component would
        // need its own nested `<RouterView>` for the children to mount — but
        // the running↔result distinction is a state flip inside the SAME view,
        // not a nested layout, so the leaves own the component and the parent
        // only contributes the `:variant` segment + the shared `result` path.
        path: "/:variant(test|school|home)?",
        children: [
            {
                path: "",
                name: "speedtest",
                component: () => import("@src/views/SpeedtestView.vue"),
            },
            {
                path: "result",
                name: "speedtest-result",
                component: () => import("@src/views/SpeedtestView.vue"),
                // Cold deep-link onto a result URL with an empty store
                // redirects to the running route — the URL is real, the
                // state gate holds.
                beforeEnter: resultGuard,
            },
        ],
    },
    {
        path: "/survey",
        name: "survey",
        component: () => import("@src/views/SurveyView.vue"),
    },
    {
        path: "/thankyou",
        name: "thankyou",
        component: () => import("@src/views/ThankYouView.vue"),
    },

    // ── Public dashboard ─────────────────────────────────────────────
    {
        path: "/dashboard",
        component: () => import("@src/layouts/PublicDashboardLayout.vue"),
        children: [
            {
                path: "",
                name: "dashboard",
                redirect: { name: "dashboard-map" },
            },
            {
                path: "map",
                name: "dashboard-map",
                component: () => import("@src/views/MapView.vue"),
            },
            {
                path: "charts",
                name: "dashboard-charts",
                component: () => import("@src/views/ChartsView.vue"),
            },
        ],
    },

    // ── Admin login (exempt from the auth gate) ──────────────────────
    //
    // W10.T5: the auth entry surface is a dedicated top-level route, not
    // a `v-if`-gated branch inside `AdminDashboardLayout`. The router
    // redirects unauthenticated `/admin/*` requests here with `?next=
    // <encoded-path>` so the view can route the user back to the
    // originally requested page on successful auth.
    {
        path: "/admin/login",
        name: "admin-login",
        component: () => import("@src/views/AdminLoginView.vue"),
    },

    // ── Admin dashboard (5 tabs: Overview, Data, Charts, Map, Settings) ──
    {
        path: "/admin",
        // W10.T2: every child route inherits `requiresAdminAuth`. The
        // router-level `beforeEach` admin-auth guard reads this meta flag
        // off `to.matched` so the auth boundary is enforced at the
        // navigation layer, not the presentation layer — closes B3b BLOCKER
        // B1 (admin auth was a layout concern) + B3c CRITICAL C1 (direct
        // /admin/{tab} URL bypassed the layout-level gate on first load).
        meta: { requiresAdminAuth: true },
        component: () => import("@src/layouts/AdminDashboardLayout.vue"),
        children: [
            {
                path: "",
                name: "admin",
                redirect: { name: "admin-overview" },
            },
            {
                path: "overview",
                name: "admin-overview",
                component: () => import("@src/views/AdminOverviewView.vue"),
            },
            {
                path: "data",
                name: "admin-data",
                component: () => import("@src/views/AdminDataView.vue"),
            },
            {
                path: "charts",
                name: "admin-charts",
                component: () => import("@src/views/ChartsView.vue"),
            },
            {
                path: "map",
                name: "admin-map",
                component: () => import("@src/views/MapView.vue"),
            },
            {
                path: "settings",
                name: "admin-settings",
                component: () => import("@src/views/AdminSettingsView.vue"),
            },

            // ── Redirects for old routes ─────────────────────────────
            { path: "results", redirect: { name: "admin-data" } },
            { path: "sessions", redirect: { name: "admin-data" } },
            { path: "subnets", redirect: { name: "admin-settings" } },
            { path: "servers", redirect: { name: "admin-settings" } },
        ],
    },

    // ── Catch-all ────────────────────────────────────────────────────
    {
        path: "/:pathMatch(.*)*",
        redirect: "/",
    },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

// Guards run outside Vue's reactive context — read hostname directly and
// mirror the registry resolution. The dpi variant has no survey/thankyou pages
// and no school/home audiences; redirect every gated path to root.
//
// DDR-AV-ROUTE — variant/param-based, NOT the old route-NAME-set. Under the
// single parameterized record the `speedtest`/`speedtest-result` names are
// shared across all four variants, so a name-set could not distinguish
// school/home (gated) from test/`/` (allowed). The guard now redirects when
// the resolved variant is dpi AND either:
//   • `to.params.variant` ∈ {school, home}  (the survey-bearing audiences), or
//   • `to.matched` includes a survey/thankyou record  (the survey flow).
// This MUST stay in the same commit as the topology collapse — a collapse
// without this rewrite ships the dpi gate FAILED OPEN (P0).
router.beforeEach((to: RouteLocationNormalizedGeneric) => {
    const hostname =
        typeof window !== "undefined" && window.location
            ? window.location.hostname
            : "";
    const variantParam = readVariantParam(to.params);
    // dpi is a hostname overlay, resolved before route meta — pass the
    // param-derived meta so the resolution mirrors `useVariantStore` exactly.
    const variantId = resolveVariantId(
        hostname,
        variantMetaFromParam(variantParam),
    );

    if (variantId !== "dpi") return true;

    const hitsGatedVariant =
        variantParam !== undefined &&
        SURVEY_GATED_VARIANT_PARAMS.has(variantParam);
    const hitsGatedRecord = to.matched.some(
        (record) =>
            typeof record.name === "string" &&
            SURVEY_GATED_RECORD_NAMES.has(record.name),
    );

    if (hitsGatedVariant || hitsGatedRecord) {
        return { name: "speedtest" as const };
    }
    return true;
});

/**
 * Admin-auth guard (W10.T2).
 *
 * Every route under `/admin` (except the `/admin/login` sibling) carries
 * `meta: { requiresAdminAuth: true }` on its parent record; `to.matched`
 * walks the full record chain so a deep child (e.g., `/admin/data`)
 * inherits the parent's gate without re-declaring the flag. When the
 * guard fires for a protected route and `useAdminAuthStore.isAuthenticated`
 * is false, we redirect to the dedicated `/admin/login` view with
 * `?next=<full path>` so the login surface can route back to the
 * originally requested page after successful auth.
 *
 * This guard registers AFTER the variant guard so the dpi-variant
 * redirect (which short-circuits to `/`) wins for hostnames that have
 * no admin surface at all. The order is intentional: variant rules
 * trump auth rules.
 *
 * Closes B3b B1 (auth boundary moves from layout to router) + B3c C1
 * (deep-link /admin/{tab} on first load now passes through the guard
 * regardless of layout mount order).
 */
router.beforeEach((to) => {
    const requiresAdminAuth = to.matched.some(
        (record) => record.meta?.requiresAdminAuth === true,
    );
    if (!requiresAdminAuth) return true;

    const auth = useAdminAuthStore();
    if (auth.isAuthenticated) return true;

    // Encode the requested path so the login view can land us back here
    // post-auth. `to.fullPath` preserves query + hash; the receiver
    // validates that `next` starts with `/` before navigating.
    return {
        name: "admin-login",
        query: { next: to.fullPath },
    };
});
